<template>
  <div 
    ref="transformElement"
    class="media-card-layer-transform" 
    :style="{ ...containerStyles, ...transformStyles }"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick, computed } from "vue";

interface ImageProps {
  zoom?: number;
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
}

interface VideoProps {
  zoom?: number;
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
}

interface Props {
  // Объект со всеми вычислениями из useMediaCard
  mediaCardState: ReturnType<(...args: any[]) => any>;
  mediaType?: "image" | "video";
  imageProps?: ImageProps;
  videoProps?: VideoProps;
  objectFit?: "cover" | "contain";
  shadowStyle?: string;
}

const props = defineProps<Props>();

const containerStyles = computed(() => {
  const styles = {
    ...(props.mediaCardState?.effectsStyles?.value || {}),
  };
  
  // Применяем shadow style если он передан
  if (props.shadowStyle) {
    styles.filter = props.shadowStyle;
  }
  
  // В обоих режимах центрируем контент для правильного позиционирования
    styles.display = 'flex';
    styles.alignItems = 'center';
    styles.justifyContent = 'center';
    // Устанавливаем transform-origin на центр для правильного масштабирования и смещения
    styles.transformOrigin = 'center';
  
  return styles;
});

const LOG_PREFIX = "[MediaTransformLayer]";
const transformElement = ref<HTMLElement | null>(null);
const transformStyles = ref<Record<string, string>>({});

// Reference = размеры исходного медиа (как в TrainerPage, но подставляем из props)
const mediaReference = ref({ width: 0, height: 0 });
const maskScale = ref({ x: 1, y: 1 });
let resizeObserver: ResizeObserver | null = null;
let mediaListeners: Array<() => void> = [];
let isAlive = true;

const waitForParentWithSize = async (initialDelay = 16, maxDelay = 250) => {
  let delay = initialDelay;

  while (isAlive) {
    const parent = transformElement.value?.parentElement;
    if (parent && parent.clientWidth > 0 && parent.clientHeight > 0) {
      return parent;
    }

    await new Promise<void>((resolve) => setTimeout(resolve, delay));
    delay = Math.min(delay * 1.5, maxDelay);
  }

  return null;
};

const resolveMediaReference = async () => {
  const width =
    props.mediaType === "image"
      ? props.imageProps?.width
      : props.videoProps?.width;
  const height =
    props.mediaType === "image"
      ? props.imageProps?.height
      : props.videoProps?.height;

  if (width && height) {
    mediaReference.value = { width, height };
    return;
  }

  const parent = await waitForParentWithSize();
  if (parent) {
    mediaReference.value = {
      width: parent.clientWidth,
      height: parent.clientHeight,
    };
  }
};

const updateMaskScale = async (width: number, height: number) => {
  if (mediaReference.value.width === 0 || mediaReference.value.height === 0) {
    await resolveMediaReference();
    }

  if (mediaReference.value.width === 0 || mediaReference.value.height === 0) {
    return;
  }

  const nextScale = {
    x: Math.round((width / mediaReference.value.width) * 10000) / 10000,
    y: Math.round((height / mediaReference.value.height) * 10000) / 10000,
  };

  if (
    Math.abs(nextScale.x - maskScale.value.x) > 0.0001 ||
    Math.abs(nextScale.y - maskScale.value.y) > 0.0001
  ) {
    maskScale.value = nextScale;
    updateTransform();
  }
};

const updateTransform = () => {
  if (!transformElement.value) return;

  const scale = maskScale.value;

  if (props.mediaType === "image" && props.imageProps) {
    const { zoom = 100, positionX = 0, positionY = 0 } = props.imageProps;
    const scaledX = Number((positionX * scale.x).toFixed(3));
    const scaledY = Number((positionY * scale.y).toFixed(3));
    
    // Застосовуємо зум та зміщення для обох режимів
    const zoomValue = Number((zoom / 100).toFixed(4));
    const newTransform = `scale(${zoomValue}) translate3d(${scaledX}px, ${scaledY}px, 0px)`;
    if (transformStyles.value.transform !== newTransform) {
      transformStyles.value = { transform: newTransform };
    }
    return;
  }

  if (props.mediaType === "video" && props.videoProps) {
    const { zoom = 100, positionX = 0, positionY = 0 } = props.videoProps;
    const scaledX = Number((positionX * scale.x).toFixed(3));
    const scaledY = Number((positionY * scale.y).toFixed(3));

    // Застосовуємо зум та зміщення для обох режимів
    const zoomValue = Number((zoom / 100).toFixed(4));
    const newTransform = `scale(${zoomValue}) translate3d(${scaledX}px, ${scaledY}px, 0px)`;
    if (transformStyles.value.transform !== newTransform) {
      transformStyles.value = { transform: newTransform };
    }
  }
};

const setupResizeObserver = async () => {
  if (!transformElement.value) return;

  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }

  const parentElement = await waitForParentWithSize();
  if (!parentElement) return;

  const observer = new ResizeObserver(async ([entry]) => {
    if (!entry) return;
    const { width, height } = entry.contentRect;
    if (width > 0 && height > 0) {
      await updateMaskScale(width, height);
    }
  });
  resizeObserver = observer;

  await updateMaskScale(parentElement.clientWidth, parentElement.clientHeight);
  if (resizeObserver === observer) {
    observer.observe(parentElement);
  } else {
    observer.disconnect();
    }
};

const setupMediaListeners = async () => {
  mediaListeners.forEach((stop) => stop());
  mediaListeners = [];

  const root = await waitForParentWithSize();
  if (!root) return;

  const elements = root.querySelectorAll("img, video");

  elements.forEach((element) => {
    const handler = async () => {
      await resolveMediaReference();
      const { clientWidth, clientHeight } = root;
      if (clientWidth > 0 && clientHeight > 0) {
        updateMaskScale(clientWidth, clientHeight);
  }
    };

    element.addEventListener("load", handler);
    element.addEventListener("loadedmetadata", handler);
    mediaListeners.push(() => {
      element.removeEventListener("load", handler);
      element.removeEventListener("loadedmetadata", handler);
    });
  });
};

onMounted(async () => {
  await nextTick();
  await resolveMediaReference();
  await setupResizeObserver();
  await setupMediaListeners();
  updateTransform();
});

onUnmounted(() => {
  isAlive = false;
  mediaListeners.forEach((stop) => stop());
  mediaListeners = [];
  if (resizeObserver && transformElement.value?.parentElement) {
    resizeObserver.unobserve(transformElement.value.parentElement);
    resizeObserver.disconnect();
  }
});

watch(
  () => [props.imageProps?.zoom, props.imageProps?.positionX, props.imageProps?.positionY],
  () => updateTransform(),
  { deep: true }
);

watch(
  () => [props.videoProps?.zoom, props.videoProps?.positionX, props.videoProps?.positionY],
  () => updateTransform(),
  { deep: true }
);

watch(
  () => [props.imageProps?.width, props.imageProps?.height, props.videoProps?.width, props.videoProps?.height],
  async () => {
    await resolveMediaReference();
    const parent = await waitForParentWithSize();
    if (parent) {
      await updateMaskScale(parent.clientWidth, parent.clientHeight);
    }
  },
  { deep: true }
);

watch(
  () => transformElement.value,
  async () => {
    await resolveMediaReference();
    await setupResizeObserver();
    await setupMediaListeners();
  updateTransform();
  }
);

watch(
  () => props.objectFit,
  () => {
  updateTransform();
  }
);
</script>

<style scoped>
.media-card-layer-transform {
  max-width: 100%;
}

</style>
